import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Row, Col, Card } from 'react-bootstrap';
import { authAPI } from '../utils/httpClient';
import styled from 'styled-components';
import { useI18n } from '../i18n';
import CapCaptcha from './common/CapCaptcha';

const RegisterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: var(--background-secondary);
`;

const RegisterCard = styled(Card)`
  width: 100%;
  max-width: 500px;
  border-radius: 8px;
  box-shadow: var(--shadow);
  padding: 2rem;
`;

const RegisterLogo = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    color: var(--primary);
    font-size: 2rem;
    font-weight: 500;
    margin-top: 1rem;
  }
`;

const Register = () => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaConfig, setCaptchaConfig] = useState({ enabled: false });
  const [captchaResetSignal, setCaptchaResetSignal] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken('');
    setCaptchaResetSignal((current) => current + 1);
  }, []);

  const handleCaptchaConfig = useCallback((config) => {
    setCaptchaConfig(config);
  }, []);

  const handleCaptchaError = useCallback((captchaError) => {
    const message = typeof captchaError === 'string'
      ? captchaError
      : t('auth:errors.getCaptchaFailed');
    setError(message);
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // 表单验证
    if (!username || !password || !confirmPassword || !email || !invitationCode) {
      setError(t('auth:register.fillAll'));
      setLoading(false);
      return;
    }

    if (captchaConfig.enabled && !captchaToken) {
      setError(t('auth:errors.captchaRequired'));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth:register.passwordsNotMatch'));
      setLoading(false);
      return;
    }

    try {
      // 提交注册信息
      const registerResponse = await authAPI.register({
        username,
        password,
        email,
        organization,
        invitation_code: invitationCode,
        captcha_token: captchaToken
      });

      if (registerResponse.success) {
        // Handle unified response format
        setSuccess(registerResponse.message || t('auth:register.registerSuccess'));
        // 清空表单
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setEmail('');
        setOrganization('');
        setInvitationCode('');
        resetCaptcha();
      } else {
        // Handle unified error response format
        const errorMessage = registerResponse.error?.message || registerResponse.message || t('auth:register.registerFailed');
        setError(errorMessage);
        resetCaptcha();
      }
    } catch (error) {
      console.error('注册错误:', error);
      // Handle unified error response format
      const errorMessage = error?.message || t('auth:register.registerFailed');
      setError(errorMessage);
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const switchToLogin = () => {
    navigate('/login');
  };

  return (
    <RegisterContainer>
      <Container>
        <Row className="justify-content-center">
          <Col md={6}>
            <RegisterCard>
              <RegisterLogo>
                <h1>{t('auth:register.title')}</h1>
              </RegisterLogo>
              
              {error && <Alert variant="danger">{typeof error === 'string' ? error : (error?.message || JSON.stringify(error) || '注册错误')}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth:register.username')} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('auth:register.username')}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth:register.password')} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth:register.password')}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth:register.confirmPassword')} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('auth:register.confirmPassword')}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth:register.email')} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth:register.email')}
                    required
                  />
                  <Form.Text className="text-muted">{t('auth:register.emailHint', { defaultValue: 'Email verification is required to login' })}</Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth:register.org')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder={t('auth:register.orgPlaceholder')}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth:register.invitationCode')} <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value)}
                    placeholder={t('auth:register.invitationCode')}
                    required
                  />
                  <Form.Text className="text-muted">{t('auth:register.invitationHint')}</Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-4" style={{ display: captchaConfig.enabled ? undefined : 'none' }}>
                  <CapCaptcha
                    resetSignal={captchaResetSignal}
                    onTokenChange={setCaptchaToken}
                    onConfigChange={handleCaptchaConfig}
                    onError={handleCaptchaError}
                    lang={lang}
                  />
                </Form.Group>
                
                <div className="d-grid gap-2 mb-3">
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={loading}
                  >
                    {loading ? t('auth:register.submitting') : t('auth:register.submit')}
                  </Button>
                </div>
                
                <div className="text-center">
                  <Button 
                    variant="link" 
                    onClick={switchToLogin}
                  >
                    {t('auth:register.backToLogin')}
                  </Button>
                </div>
              </Form>
            </RegisterCard>
          </Col>
        </Row>
      </Container>
    </RegisterContainer>
  );
};

export default Register;
